import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '@/utils/api';

interface User {
  id: string;
  email: string;
  name?: string;
  role?: string;
  [key: string]: any;
}

interface TenantInfo {
  tenantDisplayName: string;
  subdomain: string;
  customBranding: boolean;
  primaryColor?: string;
  secondaryColor?: string;
  customLogo?: string;
}

interface AuthContextType {
  user: User | null;
  tenantInfo: TenantInfo | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<boolean>;
  forgotPassword: (email: string) => Promise<any>;
  resetPassword: (token: string, newPassword: string) => Promise<any>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // setAuthToken now directly interacts with the API client's token management
  // const setAuthToken = (token: string) => {
  //   api.setToken(token); // Update API client with new token and store in sessionStorage
  // };

  // clearAuthToken now directly interacts with the API client's token management
  const clearAuthToken = () => {
    api.clearToken(); // Clear token from API client and sessionStorage
  };

  // Fetch tenant branding information
  useEffect(() => {
    const fetchTenantBranding = async (): Promise<void> => {
      return;
      try {
        const response = await api.getTenantBranding();
        setTenantInfo(response.tenant);

        // Apply custom branding if enabled
        if (response.tenant.customBranding) {
          if (response.tenant.primaryColor) {
            document.documentElement.style.setProperty('--primary-color', response.tenant.primaryColor);
          }
          if (response.tenant.secondaryColor) {
            document.documentElement.style.setProperty('--secondary-color', response.tenant.secondaryColor);
          }
          // Custom logo can be applied in header component via tenantInfo
        }
      } catch (error) {
        console.error('Failed to load tenant branding:', error);
        // Non-critical error - don't block user experience
      }
    };

    if (user) {
      fetchTenantBranding();
    }
  }, [user]);

  useEffect(() => {
    const verifyCurrentToken = async () => {
      // The API client now handles retrieving the token from sessionStorage on its own initialization.
      // We only proceed if the API client *thinks* it has a token, otherwise, we assume no active session.
      if (!api.getToken()) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        // Call verifyToken; the API client will use the token it already has (from sessionStorage or login)
        const response = await api.verifyToken();
        if (response.valid && response.user) {
          setUser(response.user);
        } else {
          clearAuthToken();
          setUser(null);
        }
      } catch (err: any) {
        console.error('Token verification failed:', err);
        setError(err.message || 'Failed to verify session.');
        clearAuthToken();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    verifyCurrentToken();
    // The dependency array is empty because this effect should only run once on component mount.
    // The API client's internal state (which now includes sessionStorage) is effectively the dependency.
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.login(email, password);
      if (response.token && response.user) {
        setUser(response.user);
        // The api.login method already calls api.setToken internally if successful,
        // which now handles sessionStorage.
        return true;
      } else {
        setError('Login failed: No token or user data received.');
        return false;
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      await api.logout();
      clearAuthToken(); // This now clears from sessionStorage via api client
      setUser(null);
      return true;
    } catch (err: any) {
      console.error('Logout error:', err);
      setError(err.message || 'Logout failed.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // New: Request password reset link
  const forgotPassword = async (email: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.forgotPassword(email);
      return response;
    } catch (err: any) {
      console.error('Forgot password error:', err);
      setError(err.message || 'Failed to send password reset email.');
      throw err; // Re-throw to be caught by the calling component
    } finally {
      setLoading(false);
    }
  };

  // New: Reset password with token
  const resetPassword = async (token: string, newPassword: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.resetPassword(token, newPassword);
      return response;
    } catch (err: any) {
      console.error('Reset password error:', err);
      setError(err.message || 'Failed to reset password.');
      throw err; // Re-throw to be caught by the calling component
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    tenantInfo,
    loading,
    error,
    login,
    logout,
    forgotPassword, // Add to context value
    resetPassword,  // Add to context value
    isAuthenticated: !!user,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
